/**
 * Admin dashboard for system administrators.
 * View all challenges, submissions, and moderation queue.
 * Manage content and user accounts.
 */

import { useState } from 'react';
import { AlertCircle, Eye, Trash2, CheckCircle, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Admin dashboard sections:
 * - Challenge overview (all challenges with stats)
 * - Moderation queue (submissions pending review)
 * - Flagged submissions (user-reported content)
 * - User reports and violations
 * - System logs and monitoring
 */
export function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'moderation' | 'flagged' | 'users'>(
    'overview',
  );

  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
        <p className="mt-1 text-slate-600">Manage all challenges, content, and users.</p>
      </div>

      {/* Tab navigation */}
      <div className="mb-6 border-b border-slate-200">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-brand-600 text-brand-600 border-b-2'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Challenges
          </button>
          <button
            onClick={() => setActiveTab('moderation')}
            className={`pb-4 font-medium transition-colors ${
              activeTab === 'moderation'
                ? 'border-brand-600 text-brand-600 border-b-2'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Moderation Queue
          </button>
          <button
            onClick={() => setActiveTab('flagged')}
            className={`pb-4 font-medium transition-colors ${
              activeTab === 'flagged'
                ? 'border-brand-600 text-brand-600 border-b-2'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Flagged Content
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-4 font-medium transition-colors ${
              activeTab === 'users'
                ? 'border-brand-600 text-brand-600 border-b-2'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Users
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <ChallengeOverview />}
      {activeTab === 'moderation' && <ModerationQueue />}
      {activeTab === 'flagged' && <FlaggedContent />}
      {activeTab === 'users' && <UserManagement />}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Challenge Overview
// ──────────────────────────────────────────────────────────────────────────

function ChallengeOverview() {
  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium uppercase tracking-wide text-slate-600">
            Total Challenges
          </p>
          <p className="mt-2 text-3xl font-bold text-slate-900">24</p>
          <p className="mt-2 text-xs text-slate-500">5 ending in 24h</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium uppercase tracking-wide text-slate-600">Submissions</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">482</p>
          <p className="mt-2 text-xs text-slate-500">12 pending moderation</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium uppercase tracking-wide text-slate-600">Flagged</p>
          <p className="mt-2 text-3xl font-bold text-amber-600">8</p>
          <p className="mt-2 text-xs text-slate-500">Awaiting review</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium uppercase tracking-wide text-slate-600">Users</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">342</p>
          <p className="mt-2 text-xs text-slate-500">2 suspended</p>
        </div>
      </div>

      {/* Challenges table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Challenge</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Creator</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Submissions</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Jems</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Ends</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <p className="font-medium text-slate-900">Challenge {i}</p>
                    <p className="text-xs text-slate-500">Category: Photography</p>
                  </td>
                  <td className="px-6 py-4 text-slate-600">Creator Name</td>
                  <td className="px-6 py-4 text-slate-600">24</td>
                  <td className="text-brand-600 px-6 py-4 font-semibold">312</td>
                  <td className="px-6 py-4 text-slate-600">in 18h</td>
                  <td className="px-6 py-4">
                    <button className="text-brand-600 hover:text-brand-700 text-xs font-medium">
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-sm text-slate-500">TODO: Wire up to actual data from API</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Moderation Queue
// ──────────────────────────────────────────────────────────────────────────

function ModerationQueue() {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
        <div>
          <p className="font-semibold text-blue-900">Moderation Queue</p>
          <p className="mt-1 text-sm text-blue-700">
            12 submissions pending automated Azure Content Safety review. Cron runs every 5 minutes.
          </p>
        </div>
      </div>

      {/* Queue items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-4">
            <img
              src="https://picsum.photos/80/80?random=100"
              alt="Submission"
              className="h-20 w-20 flex-shrink-0 rounded-lg object-cover"
            />
            <div className="flex-1">
              <p className="font-medium text-slate-900">Submission {i}</p>
              <p className="mt-1 text-sm text-slate-600">
                Awaiting moderation • Queued 5 min ago • Type: image
              </p>
            </div>
            <button className="text-brand-600 hover:text-brand-700 font-medium">Review →</button>
          </div>
        ))}
      </div>

      <p className="mt-8 text-center text-sm text-slate-500">TODO: Wire up to actual queue</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Flagged Content
// ──────────────────────────────────────────────────────────────────────────

function FlaggedContent() {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold text-amber-900">User-Flagged Submissions</p>
          <p className="mt-1 text-sm text-amber-700">
            8 submissions flagged by users. Admin can approve or remove regardless of creator
            action.
          </p>
        </div>
      </div>

      {/* Flagged items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-4">
            <img
              src="https://picsum.photos/80/80?random=200"
              alt="Submission"
              className="h-20 w-20 flex-shrink-0 rounded-lg object-cover"
            />
            <div className="flex-1">
              <p className="font-medium text-slate-900">Submission {i}</p>
              <p className="mt-1 text-sm text-slate-600">
                Flagged as: violent • 3 reports • Challenge: "Best Stunt Video"
              </p>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <button className="flex items-center gap-2 rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-200">
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
              <button className="flex items-center gap-2 rounded-lg bg-red-100 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-200">
                <Trash2 className="h-4 w-4" />
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-center text-sm text-slate-500">TODO: Wire up to flag_reports table</p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// User Management
// ──────────────────────────────────────────────────────────────────────────

function UserManagement() {
  return (
    <div className="space-y-4">
      <Link
        to="/admin/locked-accounts"
        className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4 transition-colors hover:bg-red-100"
      >
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 flex-shrink-0 text-red-600" />
          <div>
            <p className="font-semibold text-red-900">Locked accounts</p>
            <p className="text-sm text-red-700">
              Review accounts locked for repeated content violations. Super admin only.
            </p>
          </div>
        </div>
        <span className="text-sm font-medium text-red-700">View →</span>
      </Link>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">User</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Email</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Challenges</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Status</th>
                <th className="px-6 py-3 text-left font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">User {i}</td>
                  <td className="px-6 py-4 text-slate-600">user{i}@example.com</td>
                  <td className="px-6 py-4 text-slate-600">{5 - i}</td>
                  <td className="px-6 py-4">
                    <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-brand-600 hover:text-brand-700 text-sm font-medium">
                      Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-sm text-slate-500">TODO: Wire up to users table</p>
    </div>
  );
}
